/**
 * Cuadre de Caja — migración web de ares7070 (reporte completo del escritorio).
 *
 * Detalle de recibos del período (capital, interés y mora por recibo) más el
 * resumen completo de caja: cobros, venta contado, iniciales, incautos,
 * matrículas, cargos, venta general, ventas al por mayor, traspasos, abonos a
 * capital, gastos y pagos a incautadores. El backend usa SQL parametrizado y
 * los filtros banco/usuario/sucursal solo se aplican en las tablas que tienen
 * la columna (el escritorio reventaba filtrando por usuario en factcxcmayor).
 * Si el usuario no tiene alcance multi-sucursal (admin, permiso global
 * `versucursales` o el checkbox "Ver sucursal" del programa en el rol),
 * el servidor fuerza su sucursal.
 * Exporta a Excel e imprime con el mismo formato del escritorio. El botón
 * "Detalle completo" imprime en un solo reporte el detalle fila a fila de
 * TODAS las categorías del resumen (lo mismo que abre el clic en cada una),
 * con subtotal por categoría y los totales del cuadre al pie; el Excel lleva
 * ese mismo detalle en una segunda hoja.
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { puedeAccionPrograma, tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { fechaDMY, useApi, usePerfilSucursal, useSucursales } from "lib/api";
import { useMultiempresa, SelectorEmpresa } from "lib/multiempresa";
import { labelTipo } from "lib/documentosvehiculo";
import { imprimirListadoAgrupado } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

/** Total del cuadre por forma de pago (/api/cuadrecaja_formaspago): lo
 * recibido de cada forma suma el TOTAL RECIBIDO. */
function FormasPagoPanel({ formas }) {
  const columnas = "1fr 140px";
  let cuerpo;
  if (!formas) {
    cuerpo = (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: FP.color.textMuted }}>
        <CircularProgress size={14} color="inherit" />
        <Typography sx={{ fontSize: "0.8rem" }}>Calculando…</Typography>
      </Stack>
    );
  } else if (formas.error) {
    cuerpo = <Typography sx={{ fontSize: "0.8rem", color: "#C62828" }}>{formas.error}</Typography>;
  } else {
    const celda = { fontSize: "0.85rem", fontVariantNumeric: "tabular-nums" };
    const fila = (label, recibido, total) => (
      <Box
        key={label}
        sx={{
          display: "grid",
          gridTemplateColumns: columnas,
          gap: 1,
          py: 0.7,
          borderTop: total ? `1px solid ${FP.color.border}` : "none",
        }}
      >
        <Typography sx={{ ...celda, fontWeight: total ? 800 : 700 }}>{label}</Typography>
        <Typography sx={{ ...celda, fontWeight: 800, textAlign: "right" }}>
          {fmtMoney(recibido)}
        </Typography>
      </Box>
    );
    cuerpo = (
      <>
        <Box sx={{ display: "grid", gridTemplateColumns: columnas, gap: 1, pb: 0.4 }}>
          <span />
          {["RECIBIDO"].map((h) => (
            <Typography
              key={h}
              sx={{
                fontSize: "0.68rem",
                fontWeight: 800,
                color: FP.color.textMuted,
                textAlign: "right",
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>
        {formas.filas.map((f) => fila(String(f.formapago).toUpperCase(), f.recibido))}
        {fila("TOTAL", formas.totalrecibido, true)}
      </>
    );
  }
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        border: `1px solid ${FP.color.border}`,
        borderRadius: 2,
        p: 2,
        bgcolor: "#fff",
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
          mb: 1.2,
        }}
      >
        Por forma de pago
      </Typography>
      {cuerpo}
    </Box>
  );
}

const moneyCol = (field, headerName, width = 120) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

const padXFila = (f) => {
  if (f.destacado) return 1;
  if (f.sub) return 2.4;
  return 0;
};
const pesoEtiqueta = (f) => {
  if (f.bold) return 800;
  if (f.sub) return 500;
  return 600;
};
const colorEtiqueta = (f) => {
  if (f.sub) return FP.color.textMuted;
  if (f.resta) return "#C62828";
  return FP.color.textPrimary;
};
const colorValor = (f) => {
  if (f.resta) return "#C62828";
  if (f.destacado) return FP.color.navy;
  return FP.color.textPrimary;
};

/** Diferencias del cuadre contra los conteos de caja guardados (solo con
 * varinicial.compararconteocuadre). Diferencia = conteo − sistema: positiva
 * es sobrante, negativa faltante; menos de un peso se toma como cuadrado. */
function ComparacionConteo({ comp }) {
  const colorDif = (d) => {
    if (Math.abs(d) < 1) return FP.color.success;
    return d > 0 ? FP.color.warning : FP.color.danger;
  };
  const textoDif = (d) => {
    if (Math.abs(d) < 1) return "Cuadra";
    return d > 0 ? "Sobrante" : "Faltante";
  };
  const celda = { fontSize: "0.82rem", py: 0.6, px: 1 };
  const num = { ...celda, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  const sinConteos = !comp.conteos || comp.conteos.length === 0;
  return (
    <Box sx={sectionBox}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "0.74rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: FP.color.textSecondary,
          }}
        >
          Comparación con el conteo de caja
        </Typography>
        {!sinConteos && (
          <Chip
            size="small"
            label={comp.cuadra ? "Cuadra" : `Diferencia RD$ ${fmtMoney(comp.diferencia)}`}
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 800,
              color: "#fff",
              bgcolor: comp.cuadra ? FP.color.success : FP.color.danger,
            }}
          />
        )}
      </Stack>
      {sinConteos ? (
        <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted }}>
          No hay conteos de caja registrados para estas fechas, usuario y sucursal.
        </Typography>
      ) : (
        <>
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ borderCollapse: "collapse", minWidth: 520 }}>
              <thead>
                <tr>
                  {["Forma de pago", "Sistema", "Conteo", "Diferencia", ""].map((h, i) => (
                    <Box
                      component="th"
                      key={h || i}
                      sx={{
                        ...(i > 0 && i < 4 ? num : celda),
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        color: FP.color.textSecondary,
                        textTransform: "uppercase",
                        borderBottom: `1px solid ${FP.color.border}`,
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comp.filas.map((f) => {
                  const total = f.formapago === "Total";
                  const peso = total ? 800 : 500;
                  return (
                    <Box
                      component="tr"
                      key={f.formapago}
                      sx={{ borderTop: total ? `1px solid ${FP.color.border}` : "none" }}
                    >
                      <Box component="td" sx={{ ...celda, fontWeight: peso }}>
                        {f.formapago}
                      </Box>
                      <Box component="td" sx={{ ...num, fontWeight: peso }}>
                        {fmtMoney(f.sistema)}
                      </Box>
                      <Box component="td" sx={{ ...num, fontWeight: peso }}>
                        {fmtMoney(f.conteo)}
                      </Box>
                      <Box
                        component="td"
                        sx={{ ...num, fontWeight: 800, color: colorDif(f.diferencia) }}
                      >
                        {fmtMoney(f.diferencia)}
                      </Box>
                      <Box
                        component="td"
                        sx={{ ...celda, fontWeight: 700, color: colorDif(f.diferencia) }}
                      >
                        {textoDif(f.diferencia)}
                      </Box>
                    </Box>
                  );
                })}
              </tbody>
            </Box>
          </Box>
          <Typography sx={{ fontSize: "0.74rem", color: FP.color.textMuted, mt: 1.4 }}>
            Conteos incluidos:{" "}
            {comp.conteos
              .map(
                (c) =>
                  `#${c.nocuadre} (${c.fecha}${c.turno ? `, turno ${c.turno}` : ""}, ${
                    c.usuario
                  }, RD$ ${fmtMoney(c.total)})`
              )
              .join(" · ")}
          </Typography>
        </>
      )}
    </Box>
  );
}

/** Filas del resumen en el orden del escritorio (ares7070). `key` enlaza la
 * categoría con su detalle fila a fila en `detalles` (backend). */
const filasResumen = (r) => {
  if (!r) return [];
  const filas = [
    { label: "COBROS POR CAJA", valor: r.cobroscaja },
    { label: "· Capital vehículo", valor: r.cobroscapital, sub: true },
    { label: "· Interés", valor: r.cobrosinteres, sub: true },
    { label: "· Mora", valor: r.cobrosmora, sub: true },
    { label: "VENTA CONTADO", valor: r.ventacontado, key: "ventacontado" },
    { label: "COBROS INICIAL", valor: r.cobrosinicial, key: "cobrosinicial" },
    { label: "INCAUTOS", valor: r.incautos, key: "incautos" },
    { label: "MATRÍCULAS", valor: r.matriculas, key: "matriculas" },
    { label: "CARGOS", valor: r.cargos, key: "cargos" },
    { label: "V. GENERAL", valor: r.ventageneral, key: "ventageneral" },
    { label: "VENTAS EFECTIVO MAYOR", valor: r.ventascontadomayor, key: "ventascontadomayor" },
    { label: "PAGOS X MAYOR CRÉDITO", valor: r.pagosmayorcredito, key: "pagosmayorcredito" },
    { label: "PAGOS X TRASPASO", valor: r.pagostraspaso, key: "pagostraspaso" },
  ];
  // Categoría propia: matrícula/placa que el cliente pagó dentro de sus
  // cuotas. El backend ya la descontó de capital y de abonos a capital, así
  // que suma al total sin contar dos veces el mismo peso. Va junto a
  // MATRÍCULAS, que es la cobrada en ventanilla.
  if (toNum(r.documentosencuotas) > 0) {
    const pos = filas.findIndex((f) => f.key === "matriculas") + 1;
    const nuevas = [
      {
        label: "MATRÍCULA/PLACA EN CUOTAS",
        valor: r.documentosencuotas,
        key: "documentosencuotas",
      },
    ];
    const pordoc = r.documentosencuotaspordoc || [];
    if (pordoc.length > 1) {
      pordoc.forEach((d) => {
        nuevas.push({ label: `· ${labelTipo(d.tipodocumento)}`, valor: d.monto, sub: true });
      });
    }
    filas.splice(pos, 0, ...nuevas);
  }
  if (toNum(r.abonosacapital) > 0) {
    filas.push({ label: "ABONOS A CAPITAL", valor: r.abonosacapital, key: "abonosacapital" });
  }
  filas.push({ label: "TOTAL RECIBIDO", valor: r.totalrecibido, bold: true });
  filas.push({ label: "GASTOS", valor: r.gastos, resta: true, key: "gastos" });
  filas.push({
    label: "PAGOS INCAUTADORES",
    valor: r.pagosincautadores,
    resta: true,
    key: "pagosincautadores",
  });
  // Compra informal de chasis: se le paga al vendedor desde la caja de la
  // sucursal donde entra el chasis, así que resta como los gastos.
  if (toNum(r.comprasinformales) > 0) {
    filas.push({
      label: "COMPRAS INFORMALES",
      valor: r.comprasinformales,
      resta: true,
      key: "comprasinformales",
    });
  }
  filas.push({ label: "TOTAL NETO", valor: r.totalneto, bold: true, destacado: true });
  return filas;
};

// Cancelados/anulados: se listan para el control, pero no suman.
const sxFilaCancelada = {
  "& .fila-cancelada": { color: FP.color.textMuted, bgcolor: "#FFF5F5" },
  "& .fila-cancelada .MuiDataGrid-cell--textRight": { textDecoration: "line-through" },
};

const CABECERAS_DETALLE = ["Fecha", "Ref.", "Cliente", "Concepto", "Valor", "Suc / User"];

/** Grupos del "detalle completo": una sección por categoría del resumen con
 * registros, en el orden del resumen, con la forma unificada de `detalles`.
 * Los cobros por caja (que bajan aparte, por recibo) se llevan a esa misma
 * forma: referencia = recibo, concepto = cuota y desglose capital/interés/mora.
 *
 * Cada recibo va por su TOTAL: capital bruto + interés + mora más los cargos,
 * incautos y completivos del inicial que ese mismo recibo cobró. No se parte
 * en matrícula/placa en cuotas ni cargos: esas categorías del resumen son el
 * mismo dinero de los recibos y abonos, así que aquí no se listan aparte. Con
 * eso los subtotales de los grupos suman exacto al TOTAL RECIBIDO. En todas
 * las categorías la referencia es el número de recibo (o de factura en las
 * ventas), que es lo que el cliente tiene en la mano. */
const CATEGORIAS_SIN_GRUPO = new Set(["documentosencuotas", "cargos"]);

/** Categorías cuyas filas traen `norecibo` (el recibo de cuota que las
 * cobró) y se funden en ese recibo; el resto de sus filas sigue en su grupo. */
const ANEXOS_RECIBO = [
  { key: "cargos", etiqueta: "Cargos" },
  { key: "incautos", etiqueta: "Incauto" },
  { key: "cobrosinicial", etiqueta: "Completivo" },
];

/** Recibos de cuota cancelados del período: el backend los manda aparte
 * (`reciboscancelados`) y solo se listan, marcados; no suman en ningún total. */
const TOTAL_RECIBO = (r) => toNum(r.vpagcap) + toNum(r.vpagint) + toNum(r.vpagmora);
const notaCancelado = (r) =>
  `CANCELADO${r.canceladopor ? ` por ${r.canceladopor}` : ""}${
    r.fechacancelado ? ` el ${r.fechacancelado}` : ""
  }`;
/** Lo anulado de cada categoría (`anulados[key]` del backend): mismo trato,
 * se lista con su monto original y no suma. */
const notaAnulado = (d) =>
  `ANULADO${d.anuladopor ? ` por ${d.anuladopor}` : ""}${
    d.fechaanulado ? ` el ${d.fechaanulado}` : ""
  }${d.motivo ? ` (${d.motivo})` : ""}`;
const conceptoAnulado = (d) => [notaAnulado(d), d.concepto].filter(Boolean).join(" · ");

export const gruposDetalle = (resumen, recibos, detalles, cancelados = [], anulados = {}) => {
  const sucUser = (r) => [r.sucursal, r.user].filter(Boolean).join(" / ");
  const grupos = [];
  // Lo cobrado dentro de un recibo de cuota (cargos, incautos, completivos
  // del inicial; el backend trae en `norecibo` el recibo que lo cobró) se
  // suma a ese recibo. Lo que no casa con un recibo de este cuadre se queda
  // en su categoría; los cargos, que no tienen grupo propio, van como fila
  // suelta de COBROS POR CAJA, para que el subtotal siga siendo el efectivo.
  const numRecibos = new Set(recibos.map((r) => String(r.norecibo)));
  const anexosPorRecibo = new Map(); // norecibo -> {key: monto}
  const fundidas = new Set(); // filas de `detalles` ya sumadas a un recibo
  ANEXOS_RECIBO.forEach(({ key }) => {
    (detalles[key] || []).forEach((d) => {
      const ref = String(d.norecibo || "");
      if (!ref || !numRecibos.has(ref)) return;
      const acc = anexosPorRecibo.get(ref) || {};
      acc[key] = (acc[key] || 0) + toNum(d.valor);
      anexosPorRecibo.set(ref, acc);
      fundidas.add(d);
    });
  });
  const cargosSueltos = (detalles.cargos || []).filter((d) => !fundidas.has(d));
  if (recibos.length || cargosSueltos.length) {
    const filas = recibos.map((r) => {
      const anexos = anexosPorRecibo.get(String(r.norecibo)) || {};
      const extra = ANEXOS_RECIBO.filter(({ key }) => anexos[key])
        .map(({ key, etiqueta }) => ` · ${etiqueta} ${fmtMoney(anexos[key])}`)
        .join("");
      const montoAnexos = Object.values(anexos).reduce((acc, v) => acc + v, 0);
      return {
        fecha: r.fecha || "",
        referencia: r.norecibo,
        cliente: r.cliente || `Prést. ${r.noprest}`,
        concepto: `Prést. ${r.noprest} · Cuota ${r.nocuota} · Cap ${fmtMoney(
          r.vpagcap
        )} · Int ${fmtMoney(r.vpagint)} · Mora ${fmtMoney(r.vpagmora)}${extra}`,
        valor: toNum(r.vpagcap) + toNum(r.vpagint) + toNum(r.vpagmora) + montoAnexos,
        sucuser: sucUser(r),
      };
    });
    cargosSueltos.forEach((d) => {
      filas.push({
        fecha: d.fecha,
        referencia: d.referencia,
        cliente: d.cliente,
        concepto: `Cargo · ${d.concepto || ""}`.trim(),
        valor: toNum(d.valor),
        sucuser: sucUser(d),
      });
    });
    grupos.push({
      key: "cobroscaja",
      titulo: "COBROS POR CAJA",
      total: filas.reduce((acc, f) => acc + f.valor, 0),
      filas,
    });
  }
  const aFila = (d, concepto) => ({
    fecha: d.fecha,
    referencia: d.referencia,
    cliente: d.cliente,
    concepto,
    valor: toNum(d.valor),
    sucuser: sucUser(d),
  });
  filasResumen(resumen).forEach((f) => {
    if (!f.key || CATEGORIAS_SIN_GRUPO.has(f.key)) return;
    const filas = (detalles[f.key] || []).filter((d) => !fundidas.has(d));
    if (filas.length) {
      grupos.push({
        key: f.key,
        titulo: f.resta ? `${f.label} (salida)` : f.label,
        resta: !!f.resta,
        total: filas.reduce((acc, d) => acc + toNum(d.valor), 0),
        filas: filas.map((d) => aFila(d, d.concepto)),
      });
    }
    // Lo anulado de la categoría va justo después, aparte y sin sumar.
    const anuladas = anulados[f.key] || [];
    if (anuladas.length) {
      grupos.push({
        key: `${f.key}-anulados`,
        titulo: `${f.label} — ANULADOS (no suman)`,
        informativo: true,
        total: anuladas.reduce((acc, d) => acc + toNum(d.valor), 0),
        filas: anuladas.map((d) => aFila(d, conceptoAnulado(d))),
      });
    }
  });
  if (cancelados.length) {
    const filas = cancelados.map((r) => ({
      fecha: r.fecha || "",
      referencia: r.norecibo,
      cliente: r.cliente || `Prést. ${r.noprest}`,
      concepto: `${notaCancelado(r)} · Prést. ${r.noprest} · Cuota ${r.nocuota}`,
      valor: TOTAL_RECIBO(r),
      sucuser: sucUser(r),
    }));
    grupos.push({
      key: "reciboscancelados",
      titulo: "RECIBOS CANCELADOS (no suman)",
      informativo: true,
      total: filas.reduce((acc, f) => acc + f.valor, 0),
      filas,
    });
  }
  return grupos;
};

const filaDetalle = (d, fmt) => [
  d.fecha,
  d.referencia,
  d.cliente,
  d.concepto,
  fmt(d.valor),
  d.sucuser,
];
const filaTotalDetalle = (etiqueta, monto) => ["", "", "", etiqueta, monto, ""];

function imprimirDetalleCompleto({
  compania,
  subtitulo,
  filtrosTxt,
  resumen,
  recibos,
  detalles,
  cancelados,
  anulados,
}) {
  const grupos = gruposDetalle(resumen, recibos, detalles, cancelados, anulados);
  imprimirListadoAgrupado({
    compania,
    titulo: "Cuadre de Caja — Detalle completo",
    subtitulo: `${subtitulo} · ${filtrosTxt}`,
    cabeceras: CABECERAS_DETALLE,
    grupos: grupos.map((g) => ({
      titulo: g.titulo,
      resumen: `${g.filas.length} registro${g.filas.length === 1 ? "" : "s"}`,
      filas: g.filas.map((d) => filaDetalle(d, fmtMoney)),
      totales: filaTotalDetalle(
        g.informativo ? "No suman" : `Subtotal ${g.titulo}`,
        g.resta ? `(${fmtMoney(g.total)})` : fmtMoney(g.total)
      ),
    })),
    totales: filaTotalDetalle("TOTAL RECIBIDO", fmtMoney(resumen?.totalrecibido)),
    totalesextra: [
      filaTotalDetalle("GASTOS", `(${fmtMoney(resumen?.gastos)})`),
      filaTotalDetalle("PAGOS INCAUTADORES", `(${fmtMoney(resumen?.pagosincautadores)})`),
      ...(toNum(resumen?.comprasinformales) > 0
        ? [filaTotalDetalle("COMPRAS INFORMALES", `(${fmtMoney(resumen?.comprasinformales)})`)]
        : []),
      filaTotalDetalle("TOTAL NETO", fmtMoney(resumen?.totalneto)),
    ],
    numericas: [4],
    pagina: "letter landscape",
  });
}

function imprimirCuadre({
  compania,
  subtitulo,
  filtrosTxt,
  recibos,
  cancelados = [],
  resumen,
  detallegastos,
  formas,
}) {
  const trRecibos = recibos
    .map(
      (r) =>
        `<tr>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
            r.norecibo
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
            r.noprest
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
            r.cliente || ""
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
            r.nocuota
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px;text-align:right">${fmtMoney(
            r.vpagcap
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px;text-align:right">${fmtMoney(
            r.vpagint
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px;text-align:right">${fmtMoney(
            r.vpagmora
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px;text-align:right">${fmtMoney(
            toNum(r.vpagcap) + toNum(r.vpagint) + toNum(r.vpagmora)
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
            r.formapago || ""
          )}</td>
          <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
            [r.sucursal, r.user].filter(Boolean).join(" / ")
          )}</td>
        </tr>`
    )
    .join("");

  const td = "padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px;color:#777";
  const canceladosHtml = cancelados.length
    ? `<p style="margin:12px 0 4px;font-weight:bold;font-size:11px">Recibos cancelados (no suman)</p>
      <table style="width:100%;border-collapse:collapse"><tbody>${cancelados
        .map(
          (r) => `<tr>
          <td style="${td}">${esc(r.norecibo)}</td>
          <td style="${td}">${esc(r.noprest)}</td>
          <td style="${td}">${esc(r.cliente || "")}</td>
          <td style="${td}">${esc(r.nocuota)}</td>
          <td style="${td};text-align:right;text-decoration:line-through">${fmtMoney(
            TOTAL_RECIBO(r)
          )}</td>
          <td style="${td}">${esc(notaCancelado(r))}</td>
          <td style="${td}">${esc([r.sucursal, r.user].filter(Boolean).join(" / "))}</td>
        </tr>`
        )
        .join("")}</tbody></table>`
    : "";

  const trResumen = filasResumen(resumen)
    .map((f) => {
      const peso = f.bold ? "bold" : "normal";
      const pad = f.sub ? "2px 6px 2px 22px" : "3px 6px";
      const valor = f.resta ? `(${fmtMoney(f.valor)})` : fmtMoney(f.valor);
      const borde = f.bold ? "border-top:1px solid #000;" : "";
      const fondo = f.destacado ? "background:#eee;" : "";
      return `<tr style="${fondo}">
        <td style="padding:${pad};font-size:11px;font-weight:${peso};${borde}">${esc(f.label)}</td>
        <td style="padding:${pad};font-size:11px;font-weight:${peso};text-align:right;${borde}">${valor}</td>
      </tr>`;
    })
    .join("");

  const formasHtml = formas?.filas?.length
    ? `<p style="margin:16px 0 4px;font-weight:bold;font-size:12px">Por Forma de Pago</p>
      <table style="width:60%;border-collapse:collapse">
        <thead><tr>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">FORMA</th>
          <th style="border-bottom:1px solid #000;text-align:right;padding:3px 6px;font-size:10px">RECIBIDO</th>
        </tr></thead>
        <tbody>${formas.filas
          .map(
            (f) => `<tr>
          <td style="padding:3px 6px;font-size:11px">${esc(f.formapago)}</td>
          <td style="padding:3px 6px;font-size:11px;text-align:right">${fmtMoney(f.recibido)}</td>
        </tr>`
          )
          .join("")}
        <tr>
          <td style="padding:3px 6px;font-size:11px;font-weight:bold;border-top:1px solid #000">TOTAL</td>
          <td style="padding:3px 6px;font-size:11px;font-weight:bold;text-align:right;border-top:1px solid #000">${fmtMoney(
            formas.totalrecibido
          )}</td>
        </tr></tbody>
      </table>`
    : "";

  const gastosHtml = detallegastos.length
    ? `<p style="margin:16px 0 4px;font-weight:bold;font-size:12px">Detalle de Gastos</p>
      <table style="width:60%;border-collapse:collapse">
        <thead><tr>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">TIPO</th>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">DESCRIPCIÓN</th>
          <th style="border-bottom:1px solid #000;text-align:right;padding:3px 6px;font-size:10px">VALOR</th>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">SUC.</th>
        </tr></thead>
        <tbody>${detallegastos
          .map(
            (g) => `<tr>
              <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
                g.tipogastos
              )}</td>
              <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
                g.descripcion
              )}</td>
              <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px;text-align:right">${fmtMoney(
                g.valor
              )}</td>
              <td style="padding:2px 6px;border-bottom:1px solid #ddd;font-size:10px">${esc(
                g.sucursal
              )}</td>
            </tr>`
          )
          .join("")}</tbody>
      </table>`
    : "";

  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Cuadre de Caja</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px">
      <h3 style="text-align:center;margin:0 0 2px">${esc(compania?.nombre || "")}</h3>
      ${
        compania?.direccion
          ? `<p style="text-align:center;margin:0;font-size:11px">${esc(compania.direccion)}</p>`
          : ""
      }
      ${
        compania?.rnc
          ? `<p style="text-align:center;margin:0;font-size:11px">RNC: ${esc(compania.rnc)}</p>`
          : ""
      }
      <p style="text-align:center;margin:8px 0 2px;font-weight:bold;font-size:14px">Cuadre de Caja</p>
      <p style="text-align:center;margin:0 0 2px;font-size:12px;color:#555">${esc(subtitulo)}</p>
      <p style="text-align:center;margin:0 0 14px;font-size:11px;color:#555">${esc(filtrosTxt)}</p>
      <table style="width:100%;border-collapse:collapse">
        <thead><tr>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">RECIBO</th>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">#PREST</th>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">CLIENTE</th>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">#CUOTA</th>
          <th style="border-bottom:1px solid #000;text-align:right;padding:3px 6px;font-size:10px">CAPITAL</th>
          <th style="border-bottom:1px solid #000;text-align:right;padding:3px 6px;font-size:10px">INTERÉS</th>
          <th style="border-bottom:1px solid #000;text-align:right;padding:3px 6px;font-size:10px">MORA</th>
          <th style="border-bottom:1px solid #000;text-align:right;padding:3px 6px;font-size:10px">TOTAL</th>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">FORMA</th>
          <th style="border-bottom:1px solid #000;text-align:left;padding:3px 6px;font-size:10px">SUC/USER</th>
        </tr></thead>
        <tbody>${trRecibos}</tbody>
      </table>
      ${canceladosHtml}
      <p style="margin:16px 0 4px;font-weight:bold;font-size:12px">Resumen de Caja</p>
      <table style="width:60%;border-collapse:collapse">
        <tbody>${trResumen}</tbody>
      </table>
      ${formasHtml}
      ${gastosHtml}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;

  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

/**
 * @param embedded  Renderiza sin PageHeader ni fondo propio para usarse como
 *                  vista principal dentro de otra pantalla (p.ej. Cuadres).
 * @param filtrosIniciales  {fecha, usuario, sucursal} opcional: arranca con
 *                  esos filtros y procesa solo (p.ej. desde un conteo de
 *                  efectivo, el cuadre de ese usuario ese día).
 */
export default function CuadreCajaCompleto({ embedded = false, filtrosIniciales = null } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleDetalles, usuarioLog } = controller;
  // Sin ares7070 (entra desde el conteo de efectivo) solo ve SU cuadre: el
  // usuario queda fijo y el backend lo fuerza igual.
  const soloPropio = !tienePermisosModulo(roleDetalles, "ares7070");
  // Alcance de sucursal: lo conceden el tipo admin o el permiso global
  // `versucursales` (usePerfilSucursal), o el checkbox "Ver sucursal" del
  // programa Cuadre de Caja en el rol (flag versucursal). El backend fuerza
  // lo mismo en /api/cuadrecaja_completo; esto lo refleja en el filtro.
  const { esadmin, sucursalPropia } = usePerfilSucursal();
  const veSucursales =
    esadmin ||
    ["ares7070", "cuadrecaja", "ares7075"].some((prog) =>
      puedeAccionPrograma(roleDetalles, prog, "versucursal")
    );

  const [fecha1, setFecha1] = useState(filtrosIniciales?.fecha || hoyISO());
  const [fecha2, setFecha2] = useState(filtrosIniciales?.fecha || hoyISO());
  const [banco, setBanco] = useState("Todos");
  const [usuarioFiltro, setUsuarioFiltro] = useState(
    soloPropio ? usuarioLog || "" : filtrosIniciales?.usuario || ""
  );
  // Usuarios para el filtro (con el alcance de sucursales del cuadre).
  const [usuarios, setUsuarios] = useState([]);
  const [sucursal, setSucursal] = useState(filtrosIniciales?.sucursal || "0");
  // Sucursales vía /api/recuperasucursales (useSucursales), que no exige el
  // programa `configuracion` como /api/config_catalogo_listar: a un rol sin
  // Configuración aquel le llegaba vacío (403) y el combo solo mostraba
  // "Todas". `todas` libera las opciones también para el flag versucursal.
  // El label "2-Nombre" se separa en código y descripción para el combo.
  const sucursales = useSucursales({ todas: veSucursales }).map((s) => {
    const [codigo, ...resto] = String(s.label || "").split("-");
    return { sucursal: codigo.trim(), descripcion: resto.join("-").trim() };
  });
  // Multi-empresa: filtro por empresa y desglose (solo con el modo activo)
  const multiempresa = useMultiempresa();
  const [empresa, setEmpresa] = useState("0");
  const [desglose, setDesglose] = useState([]);

  const [recibos, setRecibos] = useState([]);
  // Recibos cancelados del período: solo se listan (tachados), no suman.
  const [recibosCancelados, setRecibosCancelados] = useState([]);
  const [detalleGastos, setDetalleGastos] = useState([]);
  // Detalle fila a fila por categoría del resumen; clic en la fila lo abre.
  const [detalles, setDetalles] = useState({});
  const [detalleCat, setDetalleCat] = useState(null); // {key, label} abierto
  // Anulados por categoría ({key: filas}): se ven en su detalle, no suman.
  const [anulados, setAnulados] = useState({});
  const [resumen, setResumen] = useState(null);
  // Diferencias contra el conteo de caja; {activo:false} sin la opción.
  const [comparacion, setComparacion] = useState(null);
  // Totales del cuadre por forma de pago (Contado / Transferencia / Tarjeta).
  const [formas, setFormas] = useState(null);
  const [sucursalAplicada, setSucursalAplicada] = useState("");
  const [compania, setCompania] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let activo = true;
    post("/api/cobropago_perfil", {}).then(({ ok, data }) => {
      if (activo && ok) setCompania(data.compania || null);
    });
    if (!soloPropio) {
      post("/api/cuadrecaja_usuarios", {}).then(({ ok, data }) => {
        if (activo && ok && Array.isArray(data.usuarios)) setUsuarios(data.usuarios);
      });
    }
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Usuario sin alcance multi-sucursal: useSucursales ya limita la lista a
  // SU sucursal; aquí solo se preselecciona (el backend igual la fuerza).
  useEffect(() => {
    if (veSucursales || !sucursalPropia || !sucursales.length) return;
    const propia = sucursales.find((s) => s.sucursal === sucursalPropia);
    if (propia) setSucursal((prev) => (prev === "0" ? propia.sucursal : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veSucursales, sucursalPropia, sucursales.length]);

  // La comparación recalcula el cuadre partido por forma de pago: va aparte
  // para no demorar el reporte principal.
  const compararConteo = () => {
    setComparacion(null);
    post("/api/cuadrecaja_comparacion", {
      fechadesde: fecha1,
      fechahasta: fecha2,
      usuariofiltro: usuarioFiltro.trim(),
      sucursal,
    }).then(({ ok, data }) => {
      if (ok) setComparacion(data);
      else if (data.Error) showAlert({ message: data.Error, color: "error" });
    });
  };

  // El cuadre partido por forma de pago: también recalcula el cuadre tres
  // veces, así que va aparte como la comparación.
  const totalizarFormas = (filtros) => {
    setFormas(null);
    // Si falla, el panel muestra el error (el resto del cuadre ya está);
    // un fallo de red también, en vez de quedarse en "Calculando…".
    post("/api/cuadrecaja_formaspago", filtros)
      .then(({ ok, data }) => {
        setFormas(ok ? data : { filas: [], error: data?.Error || "No se pudo totalizar" });
      })
      .catch(() => setFormas({ filas: [], error: "Error de red al totalizar por forma de pago" }));
  };

  const procesar = async () => {
    setLoading(true);
    try {
      const filtros = {
        fechadesde: fecha1,
        fechahasta: fecha2,
        banco,
        usuariofiltro: usuarioFiltro.trim(),
        sucursal,
        ...(multiempresa.activo ? { empresa: Number(empresa) } : {}),
      };
      const { ok, data } = await post("/api/cuadrecaja_completo", filtros);
      if (ok) {
        setRecibos(data.recibos || []);
        setRecibosCancelados(data.reciboscancelados || []);
        setDetalleGastos(data.detallegastos || []);
        setDetalles(data.detalles || {});
        setAnulados(data.anulados || {});
        setResumen(data.resumen || null);
        setSucursalAplicada(data.sucursalaplicada || "");
        setDesglose(data.desglose || []);
        compararConteo();
        totalizarFormas(filtros);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el cuadre", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  // Con filtros iniciales el reporte se genera al abrir.
  useEffect(() => {
    if (filtrosIniciales) procesar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const descSucursal = (s) => {
    if (!s || s === "0") return "Todas";
    const f = sucursales.find((x) => String(x.sucursal) === String(s));
    return f ? `${f.sucursal} — ${f.descripcion}` : String(s);
  };

  const subtitulo = `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`;
  const filtrosTxt = `Formas de Pago: ${banco} / Sucursal: ${descSucursal(
    sucursalAplicada || sucursal
  )}${usuarioFiltro.trim() ? ` / Usuario: ${usuarioFiltro.trim()}` : ""}`;

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Cuadre de Caja"],
      [subtitulo],
      [filtrosTxt],
      [],
      [
        "RECIBO",
        "#PREST",
        "CLIENTE",
        "#CUOTA",
        "CAPITAL",
        "INTERÉS",
        "MORA",
        "TOTAL",
        "FORMA DE PAGO",
        "SUCURSAL",
        "USER",
      ],
      ...recibos.map((r) => [
        r.norecibo,
        r.noprest,
        r.cliente || "",
        r.nocuota,
        toNum(r.vpagcap),
        toNum(r.vpagint),
        toNum(r.vpagmora),
        toNum(r.vpagcap) + toNum(r.vpagint) + toNum(r.vpagmora),
        r.formapago || "",
        r.sucursal,
        r.user,
      ]),
      ...(recibosCancelados.length
        ? [
            [],
            ["RECIBOS CANCELADOS (no suman)"],
            ...recibosCancelados.map((r) => [
              r.norecibo,
              r.noprest,
              r.cliente || "",
              r.nocuota,
              toNum(r.vpagcap),
              toNum(r.vpagint),
              toNum(r.vpagmora),
              TOTAL_RECIBO(r),
              notaCancelado(r),
              r.sucursal,
              r.user,
            ]),
          ]
        : []),
      [],
      ["RESUMEN DE CAJA"],
      ...filasResumen(resumen).map((f) => [f.label, f.resta ? -toNum(f.valor) : toNum(f.valor)]),
      ...(formas?.filas?.length
        ? [
            [],
            ["POR FORMA DE PAGO", "RECIBIDO"],
            ...formas.filas.map((f) => [f.formapago, toNum(f.recibido)]),
            ["TOTAL", toNum(formas.totalrecibido)],
          ]
        : []),
      ...(detalleGastos.length
        ? [
            [],
            ["DETALLE DE GASTOS"],
            ["TIPO", "DESCRIPCIÓN", "VALOR", "SUCURSAL"],
            ...detalleGastos.map((g) => [g.tipogastos, g.descripcion, toNum(g.valor), g.sucursal]),
          ]
        : []),
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "CuadreCaja");
    // Segunda hoja: el detalle fila a fila de todas las categorías, con
    // subtotal por categoría (las salidas en negativo) y totales al pie.
    const grupos = gruposDetalle(resumen, recibos, detalles, recibosCancelados, anulados);
    if (grupos.length) {
      const aoa = [["Cuadre de Caja — Detalle completo"], [subtitulo], [filtrosTxt], []];
      grupos.forEach((g) => {
        aoa.push([g.titulo]);
        aoa.push(CABECERAS_DETALLE);
        g.filas.forEach((d) => aoa.push(filaDetalle(d, (v) => v)));
        aoa.push(
          filaTotalDetalle(
            g.informativo ? "No suman" : `Subtotal ${g.titulo}`,
            g.resta ? -g.total : g.total
          )
        );
        aoa.push([]);
      });
      aoa.push(filaTotalDetalle("TOTAL RECIBIDO", toNum(resumen?.totalrecibido)));
      aoa.push(filaTotalDetalle("GASTOS", -toNum(resumen?.gastos)));
      aoa.push(filaTotalDetalle("PAGOS INCAUTADORES", -toNum(resumen?.pagosincautadores)));
      if (toNum(resumen?.comprasinformales) > 0) {
        aoa.push(filaTotalDetalle("COMPRAS INFORMALES", -toNum(resumen?.comprasinformales)));
      }
      aoa.push(filaTotalDetalle("TOTAL NETO", toNum(resumen?.totalneto)));
      const hojaDet = XLSX.utils.aoa_to_sheet(aoa);
      hojaDet["!cols"] = [
        { wch: 12 },
        { wch: 10 },
        { wch: 32 },
        { wch: 48 },
        { wch: 14 },
        { wch: 16 },
      ];
      XLSX.utils.book_append_sheet(libro, hojaDet, "Detalle completo");
    }
    XLSX.writeFile(libro, `CuadreCaja_${hoyISO()}.xlsx`);
  };

  const imprimirDetalle = () => {
    imprimirDetalleCompleto({
      compania,
      subtitulo,
      filtrosTxt,
      resumen,
      recibos,
      detalles,
      cancelados: recibosCancelados,
      anulados,
    });
  };

  const imprimir = () => {
    imprimirCuadre({
      compania,
      subtitulo,
      filtrosTxt,
      recibos,
      cancelados: recibosCancelados,
      resumen,
      detallegastos: detalleGastos,
      formas,
    });
  };

  const columnas = [
    { field: "norecibo", headerName: "Recibo", width: 110 },
    { field: "noprest", headerName: "#Prest", width: 90 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    { field: "nocuota", headerName: "#Cuota", width: 80 },
    moneyCol("vpagcap", "Capital"),
    moneyCol("vpagint", "Interés"),
    moneyCol("vpagmora", "Mora", 100),
    {
      field: "totalfila",
      headerName: "Total",
      width: 120,
      align: "right",
      headerAlign: "right",
      sortable: false,
      valueGetter: (value, row) => toNum(row.vpagcap) + toNum(row.vpagint) + toNum(row.vpagmora),
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "formapago",
      headerName: "Forma de pago",
      width: 130,
      renderCell: ({ row, value }) =>
        row.cancelado ? (
          <Tooltip title={notaCancelado(row)}>
            <Chip
              label="CANCELADO"
              size="small"
              color="error"
              variant="outlined"
              sx={{ fontWeight: 700, fontSize: "0.68rem" }}
            />
          </Tooltip>
        ) : (
          value
        ),
    },
    {
      field: "sucuser",
      headerName: "Sucursal / User",
      flex: 1,
      minWidth: 140,
      sortable: false,
      valueGetter: (value, row) => [row.sucursal, row.user].filter(Boolean).join(" / "),
    },
  ];

  const columnasGastos = [
    { field: "tipogastos", headerName: "Tipo", width: 120 },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 200 },
    moneyCol("valor", "Valor"),
    { field: "sucursal", headerName: "Sucursal", width: 120 },
  ];

  // Detalle por categoría: el backend unifica todas las fuentes a esta forma.
  // En gastos las columnas genéricas significan tipo y descripción.
  const ETIQUETAS_DETALLE = {
    gastos: { cliente: "Tipo", concepto: "Descripción" },
    comprasinformales: { cliente: "Vendedor", concepto: "Chasis" },
  };
  const etiquetasDetalle = ETIQUETAS_DETALLE[detalleCat?.key] || {
    cliente: "Cliente",
    concepto: "Concepto",
  };
  const columnasDetalle = [
    { field: "fecha", headerName: "Fecha", width: 100 },
    { field: "referencia", headerName: "Ref.", width: 90 },
    { field: "cliente", headerName: etiquetasDetalle.cliente, flex: 1, minWidth: 170 },
    { field: "concepto", headerName: etiquetasDetalle.concepto, flex: 1, minWidth: 140 },
    moneyCol("valor", "Valor"),
    {
      field: "sucuser",
      headerName: "Suc / User",
      width: 130,
      sortable: false,
      valueGetter: (value, row) => [row.sucursal, row.user].filter(Boolean).join(" / "),
    },
  ];

  const vivosDetalleCat = detalleCat ? detalles[detalleCat.key] || [] : [];
  const anuladosDetalleCat = detalleCat ? anulados[detalleCat.key] || [] : [];
  const filasDetalleCat = [
    ...vivosDetalleCat,
    ...anuladosDetalleCat.map((d) => ({ ...d, concepto: conceptoAnulado(d) })),
  ];
  // El total es solo lo vivo: lo anulado se lista tachado y no suma.
  const totalDetalleCat = vivosDetalleCat.reduce((acc, d) => acc + toNum(d.valor), 0);

  const hayDatos = recibos.length > 0 || resumen !== null;
  const resumenFilas = filasResumen(resumen);

  return (
    <Box
      component={embedded ? "section" : "main"}
      sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      {!embedded && (
        <PageHeader
          title="Cuadre de Caja"
          subtitle="Reporte completo del día: recibos, resumen de caja y gastos (ares7070)"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Forma de Pago"
                value={banco}
                onChange={(e) => setBanco(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="Todos">Todos</MenuItem>
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Transferencias">Transferencias</MenuItem>
                {/* Por ahora solo el cobro de inicial acepta tarjeta. */}
                <MenuItem value="Tarjeta">Tarjeta</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Usuario"
                value={usuarioFiltro}
                onChange={(e) => setUsuarioFiltro(e.target.value)}
                disabled={soloPropio}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              >
                {soloPropio ? <MenuItem value={usuarioFiltro}>{usuarioFiltro}</MenuItem> : null}
                {soloPropio ? null : <MenuItem value="">Todos</MenuItem>}
                {/* Con una sucursal elegida, solo sus usuarios. */}
                {usuarios
                  .filter(
                    (u) =>
                      sucursal === "0" ||
                      u.sucursal === String(sucursal) ||
                      u.usuario === usuarioFiltro
                  )
                  .map((u) => (
                    <MenuItem key={u.usuario} value={u.usuario}>
                      {u.usuario}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                sx={fpInputSx}
              >
                {(veSucursales || !sucursales.length) && <MenuItem value="0">0 — Todas</MenuItem>}
                {sucursales.map((s) => (
                  <MenuItem key={s.sucursal} value={String(s.sucursal)}>
                    {s.sucursal} — {s.descripcion}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {multiempresa.activo && (
              <Grid item xs={6} sm={3} md={2}>
                <SelectorEmpresa
                  value={empresa}
                  onChange={setEmpresa}
                  empresas={multiempresa.empresas}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip
              label="TOTAL RECIBIDO"
              value={`RD$ ${fmtMoney(resumen?.totalrecibido)}`}
              color="#2E7D32"
            />
            <ResumenChip
              label="GASTOS"
              value={`RD$ ${fmtMoney(resumen?.gastos)}`}
              color="#C62828"
            />
            <ResumenChip label="TOTAL NETO" value={`RD$ ${fmtMoney(resumen?.totalneto)}`} />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimirDetalle}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Detalle completo
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={[...recibos, ...recibosCancelados]}
              columns={columnas}
              getRowId={(r) => (r.cancelado ? `C${r.norecibo}` : r.norecibo)}
              getRowClassName={({ row }) => (row.cancelado ? "fila-cancelada" : "")}
              loading={loading}
              rowHeight={40}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{
                ...fpDataGridSx,
                "--DataGrid-overlayHeight": "220px",
                ...sxFilaCancelada,
              }}
              disableRowSelectionOnClick
            />
          </Box>
          {recibosCancelados.length > 0 && (
            <Typography sx={{ fontSize: "0.74rem", color: FP.color.textMuted, mt: 0.8 }}>
              {recibosCancelados.length} recibo
              {recibosCancelados.length === 1 ? " cancelado" : "s cancelados"} en el período
              (tachados): se muestran para control y no suman en el cuadre.
            </Typography>
          )}
        </Box>

        {resumen && comparacion?.activo && <ComparacionConteo comp={comparacion} />}

        {resumen && (
          <Box sx={sectionBox}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.74rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: FP.color.textSecondary,
                mb: 0.4,
              }}
            >
              Resumen de Caja
            </Typography>
            <Typography sx={{ fontSize: "0.76rem", color: FP.color.textMuted, mb: 1.6 }}>
              Clic en una categoría con registros para ver su detalle.
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 3, md: 6 }}
              alignItems="flex-start"
            >
              <Box sx={{ maxWidth: 460, width: "100%" }}>
                {resumenFilas.map((f) => {
                  const nVivos = f.key ? (detalles[f.key] || []).length : 0;
                  const nAnulados = f.key ? (anulados[f.key] || []).length : 0;
                  const nDetalle = nVivos + nAnulados;
                  return (
                    <Box
                      key={f.label}
                      onClick={
                        nDetalle ? () => setDetalleCat({ key: f.key, label: f.label }) : undefined
                      }
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 0.55,
                        pl: f.sub ? 2.4 : 0,
                        borderTop: f.bold ? `1px solid ${FP.color.border}` : "none",
                        bgcolor: f.destacado ? FP.color.surfaceAlt : "transparent",
                        px: padXFila(f),
                        borderRadius: f.destacado ? 1.2 : 0,
                        ...(nDetalle && {
                          cursor: "pointer",
                          "&:hover": { bgcolor: FP.color.surfaceAlt, borderRadius: 1.2 },
                        }),
                      }}
                    >
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Typography
                          sx={{
                            fontSize: f.sub ? "0.78rem" : "0.85rem",
                            fontWeight: pesoEtiqueta(f),
                            color: colorEtiqueta(f),
                          }}
                        >
                          {f.label}
                        </Typography>
                        {nAnulados > 0 && (
                          <Tooltip title="Anulados: se ven en el detalle y no suman">
                            <Chip
                              size="small"
                              label={`${nAnulados} anul.`}
                              color="error"
                              variant="outlined"
                              sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700 }}
                            />
                          </Tooltip>
                        )}
                        {nVivos > 0 && (
                          <Chip
                            size="small"
                            label={nVivos}
                            sx={{
                              height: 18,
                              fontSize: "0.66rem",
                              fontWeight: 700,
                              bgcolor: FP.color.accentBlueSoft,
                              color: FP.color.accentBlue,
                            }}
                          />
                        )}
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: f.sub ? "0.78rem" : "0.9rem",
                          fontWeight: f.bold ? 800 : 600,
                          color: colorValor(f),
                        }}
                      >
                        {f.resta ? `(${fmtMoney(f.valor)})` : fmtMoney(f.valor)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <FormasPagoPanel formas={formas} />
            </Stack>
          </Box>
        )}

        {desglose.length > 0 && (
          <Box sx={{ ...sectionBox }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.74rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: FP.color.textSecondary,
                mb: 1.6,
              }}
            >
              Desglose por Empresa
            </Typography>
            <Box sx={{ bgcolor: "#fff" }}>
              <DataGrid
                autoHeight
                rows={desglose.map((d) => ({ id: d.empresa, ...d }))}
                columns={[
                  {
                    field: "nombre",
                    headerName: "Empresa",
                    flex: 1,
                    minWidth: 160,
                    valueGetter: (value, row) => `${row.empresa} — ${row.nombre}`,
                  },
                  moneyCol("cobros", "Cobros", 120),
                  moneyCol("documentosencuotas", "Matr./Placa cuotas", 150),
                  moneyCol("iniciales", "Iniciales", 110),
                  moneyCol("ventacontado", "Venta Contado", 120),
                  moneyCol("ventasxmayor", "Ventas x Mayor", 120),
                  moneyCol("cargos", "Cargos", 100),
                  moneyCol("matriculas", "Matrículas", 110),
                  moneyCol("incautos", "Incautos", 110),
                  moneyCol("total", "Total", 130),
                ]}
                rowHeight={40}
                hideFooter
                disableRowSelectionOnClick
                sx={fpDataGridSx}
              />
            </Box>
            <Typography sx={{ mt: 1, fontSize: "0.72rem", color: FP.color.textMuted }}>
              Incluye las categorías atribuibles a una empresa (cobros, iniciales, ventas, cargos,
              matrículas e incautos); los gastos son comunes y aparecen solo en el total general.
              Los registros anteriores a la activación del modo cuentan como empresa 1.
            </Typography>
          </Box>
        )}

        {detalleGastos.length > 0 && (
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.74rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: FP.color.textSecondary,
                mb: 1.6,
              }}
            >
              Detalle de Gastos
            </Typography>
            <Box sx={{ bgcolor: "#fff" }}>
              <DataGrid
                autoHeight
                rows={detalleGastos.map((g, i) => ({ id: i, ...g }))}
                columns={columnasGastos}
                rowHeight={40}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={fpDataGridSx}
                disableRowSelectionOnClick
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Detalle de una categoría del resumen */}
      <Dialog
        open={detalleCat !== null}
        onClose={() => setDetalleCat(null)}
        maxWidth="md"
        fullWidth
      >
        {detalleCat && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
                  Detalle — {detalleCat.label}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: FP.color.navy }}>
                  RD$ {fmtMoney(totalDetalleCat)}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary }}>
                {subtitulo} · {vivosDetalleCat.length} registro
                {vivosDetalleCat.length === 1 ? "" : "s"}
                {anuladosDetalleCat.length > 0 &&
                  ` · ${anuladosDetalleCat.length} anulado${
                    anuladosDetalleCat.length === 1 ? "" : "s"
                  } (tachados, no suman)`}
              </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 1.5 }}>
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={filasDetalleCat.map((d, i) => ({ id: i, ...d }))}
                  columns={columnasDetalle}
                  getRowClassName={({ row }) => (row.anulado ? "fila-cancelada" : "")}
                  rowHeight={38}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  sx={{ ...fpDataGridSx, ...sxFilaCancelada }}
                  disableRowSelectionOnClick
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.4 }}>
              <Button onClick={() => setDetalleCat(null)} sx={{ color: FP.color.textSecondary }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
